import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import airportImage from '../../assets/airportt.png';
import { db } from '../../config/firebase';
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  selectNotifications,
  selectUnreadCount,
  selectNotificationsLoading,
  setNotificationsFromListener,
  setNotificationsLoading,
  clearNotifications,
} from '../../store/notificationsSlice';
import {
  formatRelativeTime,
  getNotificationIcon,
  navigateWithNotification,
  notificationsFromSnapshot,
} from '../../utils/notificationUtils';

const NotificationsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, initializing } = usePublicAuth();
  const { t } = useTranslation();

  const notifications = useSelector(selectNotifications);
  const unreadCount = useSelector(selectUnreadCount);
  const loading = useSelector(selectNotificationsLoading);

  const isLoading = initializing || loading;

  useEffect(() => {
    if (initializing) {
      return;
    }

    if (!user?.uid) {
      dispatch(clearNotifications());
      return;
    }

    dispatch(fetchNotifications(user.uid));

    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
    );

    dispatch(setNotificationsLoading(true));
    const unsubscribe = onSnapshot(
      notificationsQuery,
      snapshot => {
        const mapped = notificationsFromSnapshot(snapshot);
        dispatch(setNotificationsFromListener(mapped));
      },
      () => {
        dispatch(setNotificationsLoading(false));
      },
    );

    return () => unsubscribe();
  }, [dispatch, user?.uid, initializing]);

  const handleNotificationClick = notification => {
    if (!notification) return;
    if (!notification.isRead) {
      dispatch(markNotificationAsRead({ notificationId: notification.id }));
    }
    navigateWithNotification(notification, navigate);
  };

  const handleMarkAll = () => {
    if (user?.uid && unreadCount > 0) {
      dispatch(markAllNotificationsAsRead(user.uid));
    }
  };

  if (!user && !initializing) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-4xl px-4 pt-32 pb-16 text-center">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-10 shadow-xl border border-gray-100 dark:border-slate-800">
            <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-4">
              {t('notifications.title')}
            </h1>
            <p className="text-gray-600 dark:text-slate-400 mb-6">
              {t('notifications.loginDescription')}
            </p>
            <button
              onClick={() => navigate('/auth/login')}
              className="inline-flex items-center justify-center rounded-2xl bg-black dark:bg-slate-700 px-6 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:hover:bg-slate-800"
            >
              {t('common.goToLogin')}
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />

        <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[420px]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="h-16 bg-white/20 dark:bg-white/10 rounded-xl mb-4 w-3/4 animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-white/10 rounded-lg mb-2 w-full animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-white/10 rounded-lg mb-2 w-5/6 animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-white/10 rounded-lg w-4/6 animate-pulse"></div>
              </div>
              <div className="relative flex justify-center lg:justify-end">
                <div className="w-full max-w-md h-72 bg-white/20 dark:bg-white/10 rounded-2xl animate-pulse"></div>
              </div>
            </div>
          </div>
        </section>

        <main className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-16 pb-20">
          <div className="space-y-4">
            {[...Array(4)].map((_, index) => (
              <div
                key={index}
                className="rounded-3xl bg-white dark:bg-slate-900 p-6 shadow-lg border border-gray-100 dark:border-slate-800 flex gap-4 animate-pulse"
              >
                <div className="w-12 h-12 rounded-2xl bg-gray-200 dark:bg-slate-800" />
                <div className="flex-1 space-y-3">
                  <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded w-1/3" />
                  <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 dark:bg-slate-800 rounded w-1/4" />
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[420px]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.4em] text-yellow-200">
                {t('notifications.badge')}
              </p>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-4 drop-shadow-lg">
                {t('notifications.title')}
              </h1>
              <p className="text-xl text-yellow-50 mb-6 max-w-2xl">
                {t('notifications.heroDescription')}
              </p>
              <div className="flex flex-wrap items-center gap-3 text-white/90">
                <span className="rounded-full border border-white/40 px-4 py-2 text-sm font-semibold">
                  {t('notifications.total', { count: notifications.length })}
                </span>
                <span className="rounded-full border border-white/40 px-4 py-2 text-sm font-semibold">
                  {t('notifications.unread', { count: unreadCount })}
                </span>
              </div>
            </div>
            <div className="relative flex justify-center lg:justify-end">
              <img
                src={airportImage}
                alt="Notifications"
                className="w-full max-w-md h-auto rounded-2xl shadow-2xl border border-white/20"
              />
            </div>
          </div>
        </div>
        <div
          className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white/20 to-transparent"
          style={{ zIndex: 1 }}
        ></div>
      </section>

      <main className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-16 pb-20 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-3xl font-black text-gray-900 dark:text-white">
              {t('notifications.sectionTitle')}
            </h2>
            <p className="text-gray-500 dark:text-slate-400">
              {t('notifications.sectionDescription')}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleMarkAll}
              disabled={!unreadCount}
              className={`rounded-2xl border px-4 py-2 text-sm font-semibold transition ${
                unreadCount
                  ? 'border-yellow-500 dark:border-yellow-500 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
                  : 'border-gray-200 dark:border-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
              }`}
            >
              {t('notifications.markAll')}
            </button>
          </div>
        </div>

        {notifications.length === 0 ? (
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-10 shadow-xl border border-gray-100 dark:border-slate-800 text-center">
            <p className="text-2xl font-black text-gray-900 dark:text-white mb-2">
              {t('notifications.emptyTitle')}
            </p>
            <p className="text-gray-500 dark:text-slate-400 mb-6">
              {t('notifications.emptyDescription')}
            </p>
            <button
              onClick={() => navigate('/trips/sender')}
              className="inline-flex items-center justify-center rounded-2xl bg-black dark:bg-slate-700 px-6 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:hover:bg-slate-800"
            >
              {t('notifications.exploreTrips')}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {notifications.map(notification => {
              const notificationType =
                notification.data?.type || notification.type;
              const { Icon, color, bg } = getNotificationIcon(notificationType);

              return (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full rounded-3xl border bg-white dark:bg-slate-900 p-6 shadow-sm transition hover:shadow-xl text-left ${
                    notification.isRead
                      ? 'border-gray-100 dark:border-slate-800'
                      : 'border-yellow-200 dark:border-yellow-900/50 ring-2 ring-yellow-100 dark:ring-yellow-900/30'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`h-12 w-12 rounded-2xl flex items-center justify-center ${bg}`}
                    >
                      <Icon className={`h-6 w-6 ${color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <span className="text-base font-bold text-gray-900 dark:text-white">
                            {notification.title ||
                              notificationType?.replace(/_/g, ' ') ||
                              t('notifications.defaultTitle')}
                        </span>
                        {!notification.isRead && (
                          <span className="rounded-full bg-yellow-100 dark:bg-yellow-900/20 px-3 py-1 text-xs font-semibold text-yellow-700 dark:text-yellow-400">
                            {t('notifications.new')}
                          </span>
                        )}
                        {notification.data?.tripType && (
                          <span className="rounded-full bg-gray-100 dark:bg-slate-800 px-3 py-1 text-xs font-semibold text-gray-600 dark:text-slate-300">
                            {notification.data.tripType === 'shipping'
                              ? t('notifications.asSender')
                              : t('notifications.asTraveler')}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 dark:text-slate-400 text-sm sm:text-base break-words">
                        {notification.body ||
                          notification.data?.body ||
                          t('notifications.defaultBody')}
                      </p>
                      <div className="mt-3 flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-slate-400">
                        <span>
                          {formatRelativeTime(notification.createdAt)}
                        </span>
                        {notification.data?.requestId && (
                          <>
                            <span className="h-1 w-1 rounded-full bg-gray-400 dark:bg-slate-500" />
                            <span>
                              {t('notifications.requestId')}{' '}
                              <span className="font-mono">
                                {notification.data.requestId.slice(0, 6)}...
                              </span>
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default NotificationsPage;
